
/**
 * Geocodificador L10 usando Nominatim (OpenStreetMap).
 *
 * Último recurso en la cascada de geocodificación PTEL.
 * Cobertura global, confianza moderada-baja para datos españoles.
 *
 * @module services/geocoding/generic
 * @version 1.0.0
 */

import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import proj4 from 'proj4';
import type { GeocodedCoordinates } from './CartoCiudadGeocoder';
import type { GeocodingResult as TypedResult } from '../../../types/errors';
import {
  createErrorContext,
  createError,
  success,
  failure,
} from '../../../types/errors';

// Asegurar proyecciones
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
}

// ── Rate-limit por host (FASE-A, hereda BUG-01-S1) ──────────────────────────
// La compuerta vive ahora en HostRateGate (compartida por TODO llamante al
// host de Nominatim, no solo esta clase). Valor 1100 ms sin cambios.
import {
  acquireHostSlot,
  getHostMinIntervalMs,
  reportRateLimited,
  retryAfterHeaderOf,
  _resetHostRateGateForTests,
} from '../HostRateGate';
import {
  leerCacheNominatim,
  guardarAciertoNominatim,
  guardarSinResultadoNominatim,
  vaciarCacheNominatim,
  contarCacheNominatim,
} from '../cache/NominatimCache';

/** Prefijo de las claves de este cliente en la caché compartida (Overpass usa `ovp|`). */
const PREFIJO_CACHE = 'nom|';

/** Sólo para tests: resetea la compuerta compartida (compat BUG-01-S1). */
export function _resetGlobalRateLimitForTests(): void {
  _resetHostRateGateForTests();
}
// ─────────────────────────────────────────────────────────────────────────────

/** Resultado individual de Nominatim */
interface NominatimResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  class: string;
  type: string;
  place_rank: number;
  importance: number;
  addresstype: string;
  name: string;
  display_name: string;
  address?: {
    road?: string;
    house_number?: string;
    suburb?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
    [key: string]: string | undefined;
  };
  boundingbox?: [string, string, string, string];
}

/** Opciones de búsqueda Nominatim */
export interface NominatimSearchOptions {
  /** Texto de búsqueda */
  query: string;
  /** Municipio para contexto */
  municipality?: string;
  /** Provincia para contexto */
  province?: string;
  /** Máximo de resultados */
  maxResults?: number;
}

/**
 * Geocodificador L5 usando Nominatim (OpenStreetMap).
 * 
 * Último fallback en la cascada de geocodificación.
 * Cobertura global, confianza moderada-baja para datos españoles.
 * 
 * @example
 * ```typescript
 * const geocoder = new NominatimGeocoder();
 * const result = await geocoder.geocode({
 *   query: 'Centro de Salud',
 *   municipality: 'Colomera',
 *   province: 'Granada'
 * });
 * ```
 */
export class NominatimGeocoder {
  private static readonly BASE_URL = 'https://nominatim.openstreetmap.org';
  private static readonly RATE_LIMIT_MS = 1100; // >1s entre peticiones
  private axiosInstance: AxiosInstance;
  private lastRequestTime = 0;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 10000,
      headers: {
        'Accept': 'application/json',
      },
    });
  }

  /**
   * Geocodifica un nombre de lugar o dirección mediante Nominatim
   */
  public async geocode(options: NominatimSearchOptions): Promise<TypedResult<GeocodedCoordinates>> {
    const cacheKey = this.buildCacheKey(options);
    const startTime = Date.now();

    const errorContext = createErrorContext('nominatim', 'L5', {
      elementName: options.query,
      municipio: options.municipality,
      provincia: options.province,
    });

    // Caché compartida (NOMFIX-a2): aciertos y «sin resultado» (1 h), también tras recargar
    const enCache = leerCacheNominatim<GeocodedCoordinates>(cacheKey);
    if (enCache.acierto) {
      if (enCache.valor) {
        return success(enCache.valor, {
          processingTime: Date.now() - startTime,
          sourcesConsulted: ['nominatim'],
          finalSource: 'cache',
          cascadeLevel: 'L0',
          fromCache: true,
        });
      }
      return failure(
        createError.noResults(
          errorContext,
          options.query,
          { municipality: options.municipality || '', province: options.province || '' },
          true,
        ),
      );
    }

    // BUG-01-S1: Retry 3× con backoff exponencial para errores 429
    const MAX_RETRIES = 3;
    let attempt = 0;
    while (attempt < MAX_RETRIES) {
      try {
        // Compuerta por host (compartida con todo llamante a Nominatim)
        await acquireHostSlot(NominatimGeocoder.BASE_URL);

        const results = await this.search(options);
        if (!results.length) {
          guardarSinResultadoNominatim(cacheKey); // Nominatim contestó: no hay nada
          return failure(
            createError.noResults(
              errorContext,
              options.query,
              { municipality: options.municipality || '', province: options.province || '' },
              true,
            ),
          );
        }

        // Filtrar por ubicación si hay municipio/provincia
        const filtered = this.filterByLocation(results, options);
        const best = filtered.length > 0 ? filtered[0] : results[0];

        const coords = this.resultToCoordinates(best, filtered.length > 0);
        if (!coords) {
          guardarSinResultadoNominatim(cacheKey);
          return failure(
            createError.noResults(errorContext, options.query, {}, true),
          );
        }

        guardarAciertoNominatim(cacheKey, coords);
        return success(coords, {
          processingTime: Date.now() - startTime,
          sourcesConsulted: ['nominatim'],
          finalSource: 'nominatim',
          cascadeLevel: 'L5',
          fromCache: false,
        });
      } catch (error) {
        const axErr = error as AxiosError;
        if (axErr.response?.status === 429) {
          // NOMFIX-a1b: el host queda en enfriamiento para TODOS los llamantes
          // (orquestador, Overpass/telecom): `Retry-After` si vino, mínimo 2,2 s.
          reportRateLimited(NominatimGeocoder.BASE_URL, retryAfterHeaderOf(axErr));
        }
        if (axErr.response?.status === 429 && attempt < MAX_RETRIES - 1) {
          attempt++;
          // Backoff exponencial con jitter: 2.2s, 4.4s + hasta 200ms aleatorio
          // (base = intervalo del host en la compuerta compartida, 1100 ms)
          const backoff = getHostMinIntervalMs(NominatimGeocoder.BASE_URL) * Math.pow(2, attempt) + Math.random() * 200;
          await new Promise((r) => setTimeout(r, backoff));
          continue;
        }
        return this.handleError(axErr, errorContext);
      }
    }
    // Fallback de seguridad (inalcanzable en la práctica)
    return this.handleError({ response: { status: 429 } } as AxiosError, errorContext);
  }

  /**
   * Geocodifica infraestructura (wrapper para orquestador)
   */
  public async geocodeInfrastructure(
    name: string,
    municipality: string,
    province: string,
  ): Promise<TypedResult<GeocodedCoordinates>> {
    return this.geocode({ query: name, municipality, province });
  }

  /**
   * Ejecuta búsqueda en Nominatim
   */
  private async search(options: NominatimSearchOptions): Promise<NominatimResult[]> {
    const query = this.buildQuery(options);
    const limit = Math.min(options.maxResults || 5, 10);

    const params = new URLSearchParams({
      q: query,
      format: 'json',
      addressdetails: '1',
      limit: String(limit),
      countrycodes: 'es',
      'accept-language': 'es',
    });

    // Bounding box de Andalucía para priorizar resultados
    params.append('viewbox', '-7.52,39.07,-1.63,36.00');
    params.append('bounded', '0'); // No restringir, solo priorizar

    const url = `${NominatimGeocoder.BASE_URL}/search?${params.toString()}`;
    const response = await this.axiosInstance.get<NominatimResult[]>(url);

    this.lastRequestTime = Date.now();
    return response.data || [];
  }

  /**
   * Filtra resultados por coincidencia con municipio/provincia
   */
  private filterByLocation(
    results: NominatimResult[],
    options: NominatimSearchOptions,
  ): NominatimResult[] {
    if (!options.municipality && !options.province) return results;

    const muniNorm = options.municipality
      ? this.normalize(options.municipality)
      : null;
    const provNorm = options.province
      ? this.normalize(options.province)
      : null;

    return results.filter((r) => {
      const addr = r.address || {};
      const rMuni = this.normalize(
        addr.city || addr.town || addr.village || addr.municipality || '',
      );
      const rProv = this.normalize(addr.state || addr.county || '');

      const muniOk = !muniNorm || rMuni.includes(muniNorm) || muniNorm.includes(rMuni);
      const provOk = !provNorm || rProv.includes(provNorm) || provNorm.includes(rProv);

      return muniOk && provOk;
    });
  }

  /**
   * Convierte resultado Nominatim en coordenadas geocodificadas
   */
  private resultToCoordinates(
    result: NominatimResult,
    locationMatched: boolean,
  ): GeocodedCoordinates | null {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    if (isNaN(lat) || isNaN(lon)) return null;

    // WGS84 → UTM30 ETRS89
    const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [lon, lat]);

    // Confianza basada en tipo de resultado y si coincidió municipio
    const baseConfidence = this.calculateConfidence(result);
    const confidence = locationMatched ? baseConfidence : Math.max(baseConfidence - 15, 20);

    const addr = result.address || {};

    return {
      x,
      y,
      confidence,
      source: 'Nominatim',
      sourceLayer: result.type,
      matchedName: result.name || result.display_name,
      matchType: result.addresstype,
      municipality: addr.city || addr.town || addr.village || addr.municipality,
      province: addr.state,
      metadata: {
        osmType: result.osm_type,
        osmId: result.osm_id,
        placeRank: result.place_rank,
        importance: result.importance,
        displayName: result.display_name,
        class: result.class,
      },
    };
  }

  /**
   * Calcula confianza según el tipo y rango del resultado
   */
  private calculateConfidence(result: NominatimResult): number {
    // place_rank: 1-30, menor = más genérico
    // Para edificios/direcciones (rank 26-30): mayor confianza
    // Para municipios (rank 16): confianza media
    // Para regiones (rank < 16): baja confianza
    const rank = result.place_rank;

    if (rank >= 26) return 55; // Dirección/edificio específico
    if (rank >= 22) return 50; // Calle/zona
    if (rank >= 16) return 40; // Municipio
    return 30; // Región/provincia (demasiado genérico)
  }

  /**
   * Construye query de búsqueda con contexto geográfico
   */
  private buildQuery(options: NominatimSearchOptions): string {
    const parts = [options.query];
    if (options.municipality) parts.push(options.municipality);
    if (options.province) parts.push(options.province);
    return parts.join(', ');
  }

  /** @deprecated Delegado a la compuerta compartida — mantener por compatibilidad de tests */
  private async enforceRateLimit(): Promise<void> {
    await acquireHostSlot(NominatimGeocoder.BASE_URL);
    this.lastRequestTime = Date.now(); // mantener para getStats()
  }

  /** Normaliza texto para comparación */
  private normalize(text: string): string {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  }

  /** Construye clave de caché */
  private buildCacheKey(options: NominatimSearchOptions): string {
    return `${PREFIJO_CACHE}${options.query}|${options.municipality || ''}|${options.province || ''}`.toLowerCase();
  }

  /** Gestiona errores de red */
  private handleError(
    error: AxiosError,
    errorContext: ReturnType<typeof createErrorContext>,
  ): TypedResult<GeocodedCoordinates> {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return failure(createError.timeout(errorContext, NominatimGeocoder.BASE_URL, 10000));
    }
    if (error.response?.status === 429) {
      return failure(createError.rateLimit(errorContext, NominatimGeocoder.BASE_URL, 1));
    }
    if (error.response?.status && error.response.status >= 500) {
      return failure(
        createError.serviceUnavailable(errorContext, NominatimGeocoder.BASE_URL, error.response.status),
      );
    }
    return failure(createError.connection(errorContext, NominatimGeocoder.BASE_URL, 'unknown'));
  }

  /** Limpia caché */
  public clearCache(): void { vaciarCacheNominatim(PREFIJO_CACHE); }

  /** Estadísticas */
  public getStats(): { cacheSize: number; endpoint: string } {
    return { cacheSize: contarCacheNominatim(PREFIJO_CACHE), endpoint: NominatimGeocoder.BASE_URL };
  }

  /** Reset estadísticas (compatibilidad orquestador) */
  public resetStats(): void { vaciarCacheNominatim(PREFIJO_CACHE); }
}

export default NominatimGeocoder;
